import { useLearningInsights } from '@/hooks/useLearningInsights';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Lightbulb,
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  Target,
  Clock,
  BookOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function Learning() {
  const { data, isLoading, error } = useLearningInsights();

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingState message="Analyzing your performance..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={BookOpen}
          title="Something went wrong"
          description="We couldn't load your insights. Please try again."
          action={
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          }
        />
      </div>
    );
  }

  if (!data || data.totalActions === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Learning</h1>
            <p className="text-muted-foreground text-sm">
              Insights from your sales execution
            </p>
          </div>
        </div>
        <EmptyState
          icon={Lightbulb}
          title="Not enough data yet"
          description="Complete more actions and close some deals to see insights about your performance."
        />
      </div>
    );
  }

  const trendIcons = {
    up: TrendingUp,
    down: TrendingDown,
    neutral: Target,
  };

  const trendColors = {
    up: 'text-green-600',
    down: 'text-destructive',
    neutral: 'text-muted-foreground',
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Learning</h1>
          <p className="text-muted-foreground text-sm">
            Insights from your sales execution (last 30 days)
          </p>
        </div>
      </div>

      {/* Key Insights */}
      <div className="grid gap-4 md:grid-cols-3 mb-8">
        {data.insights.map((insight, index) => {
          const TrendIcon = trendIcons[insight.trend || 'neutral'];
          return (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-sm text-muted-foreground">{insight.title}</p>
                  <TrendIcon 
                    className={cn('w-4 h-4', trendColors[insight.trend || 'neutral'])} 
                  />
                </div>
                <p className="text-2xl font-bold capitalize mb-1">{insight.value}</p>
                <p className="text-xs text-muted-foreground">{insight.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Loss Reasons Breakdown */}
      {data.lossReasons.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              Loss Reasons (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.lossReasons.map(([reason, count], index) => {
                const total = data.lossReasons.reduce((sum, [, c]) => sum + c, 0);
                const percentage = Math.round((count / total) * 100);
                return (
                  <div key={reason}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{reason.replace('_', ' ')}</span>
                      <span className="text-muted-foreground">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-destructive/60 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {data.recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-primary" />
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {data.recommendations.map((rec, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-primary">{index + 1}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{rec}</p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <div className="mt-6 p-4 rounded-lg bg-muted/50 border">
        <div className="flex flex-wrap gap-6">
          <div>
            <p className="text-sm text-muted-foreground">Total Actions (30d)</p>
            <p className="text-xl font-semibold">{data.totalActions}</p>
          </div>
          <div className="w-px bg-border" />
          <div>
            <p className="text-sm text-muted-foreground">Active Deals</p>
            <p className="text-xl font-semibold">{data.totalDeals}</p>
          </div>
          <div className="w-px bg-border" />
          <div>
            <p className="text-sm text-muted-foreground">Avg Follow-up</p>
            <p className="text-xl font-semibold">{data.avgFollowUpDelay.toFixed(1)} days</p>
          </div>
        </div>
      </div>
    </div>
  );
}
